import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { X, Sparkles, Calculator, Loader2, ArrowRight, Save, FolderPlus, Package, HardHat, Clock } from 'lucide-react';

const QuickTotalCalcModal = ({ onClose }) => {
  const [step, setStep] = useState('input'); // input | loading | result | saved
  const navigate = useNavigate();
  const [form, setForm] = useState({
    description: '',
    work_type: '',
    square_meters: '',
    location: '',
    attached_text: '',
  });
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [savedCalcId, setSavedCalcId] = useState(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runCalc = async () => {
    if (form.description.trim().length < 10) {
      toast.error('Beskrivningen måste vara minst 10 tecken');
      return;
    }
    setStep('loading');
    try {
      const payload = {
        description: form.description,
        work_type: form.work_type || null,
        square_meters: form.square_meters ? parseFloat(form.square_meters) : null,
        location: form.location || null,
        attached_text: form.attached_text || null,
      };
      const res = await api.quickCalcTotal(payload);
      setResult(res.data);
      setSaveName(form.description.slice(0, 60));
      setStep('result');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI-beräkning misslyckades');
      setStep('input');
    }
  };

  const handleSave = async () => {
    if (!saveName.trim()) { toast.error('Ge beräkningen ett namn'); return; }
    setSaving(true);
    try {
      const res = await api.saveQuickCalc({
        name: saveName,
        description: form.description,
        work_type: form.work_type || null,
        square_meters: form.square_meters ? parseFloat(form.square_meters) : null,
        location: form.location || null,
        result,
      });
      setSavedCalcId(res.data.id);
      setStep('saved');
      toast.success('Beräkning sparad i arkivet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToProject = async () => {
    if (!savedCalcId) return;
    try {
      const res = await api.quickCalcToProject(savedCalcId, {
        project_name: saveName,
      });
      toast.success(`Projekt skapat: ${res.data.project_name}`);
      onClose();
      navigate(`/dashboard/projects/${res.data.project_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa projekt');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-3 py-6" onClick={onClose} data-testid="quick-total-calc-modal">
      <div className="max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col bg-card rounded-2xl shadow-2xl border border-border" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-[#E8613A] to-[#D4532F] text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center"><Calculator size={18} /></div>
            <div>
              <p className="text-[10px] uppercase tracking-widest opacity-80 flex items-center gap-1"><Sparkles size={10} /> AI</p>
              <h2 className="text-base font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Totalberäkning</h2>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-white/15 rounded p-1.5" data-testid="quick-calc-close"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto flex-1">
          {step === 'input' && (
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Få allt på en gång: material, arbete & tidsåtgång — utan att behöva skapa ett projekt.
                Du kan spara resultatet och göra om det till ett projekt senare.
              </p>
              <div>
                <Label className="text-xs">Beskrivning av jobbet *</Label>
                <textarea
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  rows={4}
                  placeholder="T.ex. Totalrenovering av 80 m² lägenhet i Stockholm: rivning, nytt golv (laminat), kakling badrum, måleri alla rum, ny köksinredning..."
                  className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  data-testid="qc-description"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Arbetstyp</Label>
                  <Input value={form.work_type} onChange={(e) => setField('work_type', e.target.value)} placeholder="Renovering / Mark / Nybyggnad" className="mt-1.5" data-testid="qc-worktype" />
                </div>
                <div>
                  <Label className="text-xs">Yta (m²)</Label>
                  <Input type="number" value={form.square_meters} onChange={(e) => setField('square_meters', e.target.value)} placeholder="80" className="mt-1.5" data-testid="qc-sqm" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Plats</Label>
                  <Input value={form.location} onChange={(e) => setField('location', e.target.value)} placeholder="Skövde" className="mt-1.5" data-testid="qc-location" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Ritningstext / dokumenttext (valfritt)</Label>
                  <textarea
                    value={form.attached_text}
                    onChange={(e) => setField('attached_text', e.target.value)}
                    rows={3}
                    placeholder="Klistra in text från ritningar, anbudsunderlag eller anteckningar för bättre precision"
                    className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    data-testid="qc-attached"
                  />
                </div>
              </div>
              <Button onClick={runCalc} className="w-full h-11 bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full font-semibold" data-testid="qc-run">
                <Sparkles size={14} className="mr-2" /> Kör AI-beräkning
              </Button>
            </div>
          )}

          {step === 'loading' && (
            <div className="p-10 text-center">
              <Loader2 size={36} className="mx-auto text-[#E8613A] animate-spin mb-3" />
              <p className="text-sm text-muted-foreground">AI räknar ut material, arbete och tid…</p>
              <p className="text-xs text-muted-foreground mt-1.5">Det tar 10–20 sekunder.</p>
            </div>
          )}

          {step === 'result' && result && (
            <div className="p-6 space-y-4" data-testid="qc-result">
              <div className="bg-gradient-to-br from-[#E8613A]/10 to-[#E8613A]/5 border border-[#E8613A]/30 rounded-xl p-4">
                <p className="text-[10px] uppercase tracking-widest text-[#E8613A] font-bold">Totalkostnad</p>
                <p className="text-3xl font-bold mt-1" data-testid="qc-total">{(result.total_cost || 0).toLocaleString('sv-SE')} kr</p>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{result.summary}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <ResultCard icon={Package} label="Material" value={`${(result.material?.total_cost || 0).toLocaleString('sv-SE')} kr`} note={`${(result.material?.items || []).length} poster`} testid="qc-material" />
                <ResultCard icon={HardHat} label="Arbete" value={`${(result.labor?.total_cost || 0).toLocaleString('sv-SE')} kr`} note={`${result.labor?.total_hours || 0} h totalt`} testid="qc-labor" />
                <ResultCard icon={Clock} label="Tid" value={`${result.time?.estimated_days || 0} dagar`} note={`${(result.time?.milestones || []).length} milstolpar`} testid="qc-time" />
              </div>

              <details className="bg-muted/30 border border-border rounded-lg">
                <summary className="cursor-pointer px-4 py-3 text-sm font-medium">Material — detaljerad lista ({(result.material?.items || []).length} poster)</summary>
                <div className="px-4 pb-3 max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="border-b border-border"><tr><th className="text-left py-1">Artikel</th><th className="text-right">Antal</th><th className="text-right">À-pris</th><th className="text-right">Summa</th></tr></thead>
                    <tbody>
                      {(result.material?.items || []).map((it, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-1">{it.name}</td>
                          <td className="text-right">{it.quantity} {it.unit}</td>
                          <td className="text-right">{(it.unit_price || 0).toLocaleString('sv-SE')}</td>
                          <td className="text-right font-medium">{(it.total || 0).toLocaleString('sv-SE')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>

              <div className="pt-2 border-t border-border">
                <Label className="text-xs">Spara i arkiv som:</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Namn på beräkning" className="flex-1" data-testid="qc-save-name" />
                  <Button onClick={handleSave} disabled={saving} className="bg-[#E8613A] hover:bg-[#D4532F] text-white whitespace-nowrap" data-testid="qc-save">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} className="mr-1.5" /> Spara</>}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {step === 'saved' && (
            <div className="p-8 text-center space-y-4" data-testid="qc-saved">
              <div className="w-16 h-16 mx-auto bg-green-50 dark:bg-green-950/30 rounded-full flex items-center justify-center">
                <Save size={28} className="text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Beräkningen är sparad!</h3>
                <p className="text-sm text-muted-foreground mt-1">Du hittar den i Snabb-arkivet under Dashboard → Snabb-arkiv.</p>
              </div>
              <div className="flex flex-col gap-2 max-w-xs mx-auto">
                <Button onClick={handleConvertToProject} className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full h-10" data-testid="qc-to-project">
                  <FolderPlus size={14} className="mr-2" /> Skapa projekt från beräkningen
                </Button>
                <Button onClick={() => navigate('/dashboard/snabb-arkiv')} variant="outline" className="rounded-full h-10" data-testid="qc-go-archive">
                  Visa arkivet <ArrowRight size={14} className="ml-2" />
                </Button>
                <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground mt-1">Stäng</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ResultCard = ({ icon: Icon, label, value, note, testid }) => (
  <div className="bg-card border border-border rounded-lg p-3" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon size={13} className="text-[#E8613A]" />
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</p>
    </div>
    <p className="text-base font-bold">{value}</p>
    <p className="text-[10px] text-muted-foreground mt-0.5">{note}</p>
  </div>
);

export default QuickTotalCalcModal;
